import { buildScaledRecipeId, parseScaledRecipeId, type Recipe } from "@/data/recipes";
import { getScaleLimits } from "@/lib/macroSolver";

export interface RebalanceMealOptionInput {
  optionIds: string[];
  targetCalories: number;
  resolveRecipe: (id: string) => Recipe | undefined;
}

interface AdjustableEntry {
  originalId: string;
  baseId: string;
  currentScale: number;
  minScale: number;
  maxScale: number;
}

interface ScaleCandidate {
  scale: number;
  calories: number;
  movement: number;
}

export interface MealOptionState {
  totalCalories: number;
  movement: number;
  picks: number[];
}

export interface MealOptionAnalysis {
  states: MealOptionState[];
  buildOptionIds: (picks: number[]) => string[];
}

const SCALE_STEP = 0.001;

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function stripFreeTag(id: string): string {
  return id.replace(/:free$/, "");
}

function getCountedCalories(recipe: Recipe | undefined): number {
  if (!recipe || recipe.category === "salad") return 0;
  return recipe.cal;
}

function buildScaleCandidates(
  entry: AdjustableEntry,
  getScaledCalories: (baseId: string, scale: number) => number,
): ScaleCandidate[] {
  const candidatesByCalories = new Map<number, ScaleCandidate>();
  const totalSteps = Math.max(1, Math.ceil((entry.maxScale - entry.minScale) / SCALE_STEP));

  const registerScale = (rawScale: number) => {
    const scale = Number(clamp(rawScale, entry.minScale, entry.maxScale).toFixed(6));
    const calories = getScaledCalories(entry.baseId, scale);
    const movement = Math.abs(scale - entry.currentScale);
    const existing = candidatesByCalories.get(calories);

    if (!existing || movement < existing.movement) {
      candidatesByCalories.set(calories, { scale, calories, movement });
    }
  };

  registerScale(entry.minScale);
  registerScale(entry.currentScale);
  registerScale(entry.maxScale);

  for (let step = 0; step <= totalSteps; step += 1) {
    const scale = entry.minScale + ((entry.maxScale - entry.minScale) * step) / totalSteps;
    registerScale(scale);
  }

  return Array.from(candidatesByCalories.values()).sort((a, b) => a.calories - b.calories || a.movement - b.movement);
}

export function analyzeMealOptionCandidates({
  optionIds,
  resolveRecipe,
}: Pick<RebalanceMealOptionInput, "optionIds" | "resolveRecipe">): MealOptionAnalysis {
  const calorieCache = new Map<string, number>();
  const adjustableEntries: AdjustableEntry[] = [];
  let fixedCountedCalories = 0;

  const getScaledCalories = (baseId: string, scale: number) => {
    const normalizedScale = Number(scale.toFixed(6));
    const scaledId = buildScaledRecipeId(baseId, normalizedScale);
    const cacheKey = stripFreeTag(scaledId);
    if (calorieCache.has(cacheKey)) return calorieCache.get(cacheKey)!;

    const calories = getCountedCalories(resolveRecipe(scaledId));
    calorieCache.set(cacheKey, calories);
    return calories;
  };

  for (const originalId of optionIds) {
    const recipe = resolveRecipe(originalId);

    if (!recipe || recipe.category === "salad") continue;
    if (originalId.endsWith(":free")) {
      fixedCountedCalories += recipe.cal;
      continue;
    }

    const { baseId, scale } = parseScaledRecipeId(stripFreeTag(originalId));
    const baseRecipe = resolveRecipe(baseId);

    if (!baseRecipe || baseRecipe.category === "salad") {
      fixedCountedCalories += recipe.cal;
      continue;
    }

    const { minScale, maxScale } = getScaleLimits(baseRecipe);
    adjustableEntries.push({
      originalId,
      baseId,
      currentScale: clamp(scale, minScale, maxScale),
      minScale,
      maxScale,
    });
  }

  if (adjustableEntries.length === 0) {
    return {
      states: [{ totalCalories: fixedCountedCalories, movement: 0, picks: [] }],
      buildOptionIds: () => optionIds,
    };
  }

  const candidateSets = adjustableEntries.map((entry) => buildScaleCandidates(entry, getScaledCalories));
  type DpState = { movement: number; picks: number[] };
  let states = new Map<number, DpState>([[fixedCountedCalories, { movement: 0, picks: [] }]]);

  for (const candidates of candidateSets) {
    const nextStates = new Map<number, DpState>();

    for (const [currentCalories, state] of states.entries()) {
      for (let candidateIndex = 0; candidateIndex < candidates.length; candidateIndex += 1) {
        const candidate = candidates[candidateIndex];
        const totalCalories = currentCalories + candidate.calories;
        const movement = state.movement + candidate.movement;
        const existing = nextStates.get(totalCalories);

        if (!existing || movement < existing.movement) {
          nextStates.set(totalCalories, {
            movement,
            picks: [...state.picks, candidateIndex],
          });
        }
      }
    }

    states = nextStates;
  }

  return {
    states: Array.from(states.entries())
      .map(([totalCalories, state]) => ({
        totalCalories,
        movement: state.movement,
        picks: state.picks,
      }))
      .sort((a, b) => a.totalCalories - b.totalCalories || a.movement - b.movement),
    buildOptionIds: (picks) => {
      const replacements = new Map<string, string[]>();

      adjustableEntries.forEach((entry, index) => {
        const pickedCandidate = candidateSets[index][picks[index]] ?? candidateSets[index][0];
        const replacementId = buildScaledRecipeId(entry.baseId, pickedCandidate.scale);
        const queue = replacements.get(entry.originalId) || [];
        queue.push(replacementId);
        replacements.set(entry.originalId, queue);
      });

      return optionIds.map((originalId) => {
        const queue = replacements.get(originalId);
        if (!queue?.length) return originalId;
        return queue.shift()!;
      });
    },
  };
}

export function pickClosestMealOptionState(states: MealOptionState[], targetCalories: number): MealOptionState | null {
  if (states.length === 0) return null;

  const roundedTarget = Math.max(0, Math.round(targetCalories));
  let bestState = states[0];

  for (const candidate of states.slice(1)) {
    const candidateDiff = Math.abs(candidate.totalCalories - roundedTarget);
    const bestDiff = Math.abs(bestState.totalCalories - roundedTarget);

    if (
      candidateDiff < bestDiff ||
      (candidateDiff === bestDiff &&
        (candidate.movement < bestState.movement ||
          (candidate.movement === bestState.movement && candidate.totalCalories < bestState.totalCalories)))
    ) {
      bestState = candidate;
    }
  }

  return bestState;
}